import React, { useEffect, useState, useRef, createContext, useContext } from 'react';
import './FormContainer.scss';
import Button from 'react-bootstrap/Button';
import { X } from 'react-feather';

// Contexto para referência do container onde modais filhos devem ser renderizados
const ModalContainerRefContext = createContext<React.RefObject<HTMLDivElement> | null>(null);
export const useModalContainerRef = () => useContext(ModalContainerRefContext);

interface FormContainerProps {
    show: boolean;
    onHide: () => void;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    children: React.ReactNode;
}

interface FormContainerHeaderProps {
    children: React.ReactNode;
    onClose?: () => void;
}

interface FormContainerBodyProps {
    children: React.ReactNode;
    noPadding?: boolean;
}

interface FormContainerFooterProps {
    children: React.ReactNode;
}

// Stack global para rastrear modais abertos
let modalIdCounter = 0;
const modalStack: number[] = [];

const FormContainer: React.FC<FormContainerProps> & {
    Header: React.FC<FormContainerHeaderProps>;
    Body: React.FC<FormContainerBodyProps>;
    Footer: React.FC<FormContainerFooterProps>;
} = ({ show, onHide, size = 'xl', children }) => {
    const [modalId] = useState(() => ++modalIdCounter);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (show) {
            // Adiciona este modal ao stack
            modalStack.push(modalId);

            return () => {
                // Remove este modal do stack quando desmontado
                const index = modalStack.indexOf(modalId);
                if (index > -1) {
                    modalStack.splice(index, 1);
                }
            };
        }
    }, [show, modalId]);

    useEffect(() => {
        if (!show) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            // Apenas o modal no topo do stack responde ao ESC
            const topmostModalId = modalStack[modalStack.length - 1];
            if (e.key === 'Escape' && topmostModalId === modalId) {
                e.preventDefault();
                e.stopPropagation();
                onHide();
            }
        };

        // Usa capture phase para ter prioridade
        document.addEventListener('keydown', handleKeyDown, true);
        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [show, onHide, modalId]);

    if (!show) return null;

    return (
        <div className="form-container-overlay">
            <div className={`form-container-dialog form-container-${size}`}>
                <div className="form-container-content" ref={containerRef}>
                    <ModalContainerRefContext.Provider value={containerRef}>
                        {children}
                    </ModalContainerRefContext.Provider>
                </div>
            </div>
        </div>
    );
};

const Header: React.FC<FormContainerHeaderProps> = ({ children, onClose }) => {
    return (
        <div className="form-container-header">
            <div className="form-container-header-content">
                {children}
            </div>
            {onClose && (
                <button className="form-container-close" onClick={onClose} type="button">
                    <X />
                </button>
            )}
        </div>
    );
};

const Body: React.FC<FormContainerBodyProps> = ({ children, noPadding }) => {
    return (
        <div className={`form-container-body ${noPadding ? 'no-padding' : ''}`}>
            {children}
        </div>
    );
};

const Footer: React.FC<FormContainerFooterProps> = ({ children }) => {
    return (
        <div className="form-container-footer">
            {children}
        </div>
    );
};

FormContainer.Header = Header;
FormContainer.Body = Body;
FormContainer.Footer = Footer;

export default FormContainer;
